import React from 'react';
import { ChevronDown, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { categories } from '@/data/exploreConstants';

export interface PublishDraftFormState {
  title: string;
  abstract: string;
  authors: string;
  keywords: string;
  category: string;
  visibility: 'public' | 'private';
}

interface Props {
  form: PublishDraftFormState;
  setForm: (next: PublishDraftFormState) => void;
  loadingPrefill: boolean;
  /** Free plans publish a PDF carrying the attribution footer. */
  showWatermarkNote: boolean;
  onUseExistingPaper: () => void;
}

/** Flat list: fields plus their specialisations, so one select is enough. */
const flatCategories = categories.flatMap((cat) =>
  cat.subcategories && cat.subcategories.length > 0
    ? cat.subcategories.map((sub) => ({ id: sub.id, name: `${cat.name} · ${sub.name}` }))
    : [{ id: cat.id, name: cat.name }],
);

const PublishDraftDialogBody: React.FC<Props> = ({
  form,
  setForm,
  loadingPrefill,
  showWatermarkNote,
  onUseExistingPaper,
}) => {
  const [showDetails, setShowDetails] = React.useState(false);
  const set = (patch: Partial<PublishDraftFormState>) => setForm({ ...form, ...patch });

  return (
    <div className="space-y-4 py-2">
      <div className="space-y-2">
        <Label>Visibility</Label>
        <RadioGroup
          value={form.visibility}
          onValueChange={(v) => set({ visibility: v as 'public' | 'private' })}
          className="flex gap-4"
        >
          <label className="flex items-center gap-2 text-sm">
            <RadioGroupItem value="public" id="wr-vis-public" />
            Public
          </label>
          <label className="flex items-center gap-2 text-sm">
            <RadioGroupItem value="private" id="wr-vis-private" />
            Private
          </label>
        </RadioGroup>
      </div>

      <p className="text-xs text-muted-foreground">
        Details you leave blank are read from the PDF after publishing. Anything you fill in
        here is kept as-is.
      </p>

      <button
        type="button"
        onClick={() => setShowDetails((v) => !v)}
        className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition"
        aria-expanded={showDetails}
      >
        <ChevronDown
          className={`h-4 w-4 transition-transform ${showDetails ? 'rotate-180' : ''}`}
          aria-hidden
        />
        Review details (optional)
      </button>

      {showDetails && (
        <div className="space-y-3">
          {loadingPrefill ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Reading your draft…
            </div>
          ) : (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="wr-pub-title">Title</Label>
                <Input
                  id="wr-pub-title"
                  value={form.title}
                  onChange={(e) => set({ title: e.target.value })}
                  placeholder="Read from the PDF if left blank"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="wr-pub-abstract">Abstract</Label>
                <Textarea
                  id="wr-pub-abstract"
                  value={form.abstract}
                  onChange={(e) => set({ abstract: e.target.value })}
                  placeholder="Read from the PDF if left blank"
                  rows={4}
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="wr-pub-authors">Authors (comma separated)</Label>
                <Input
                  id="wr-pub-authors"
                  value={form.authors}
                  onChange={(e) => set({ authors: e.target.value })}
                  placeholder="Read from the PDF if left blank"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="wr-pub-keywords">Keywords (comma separated)</Label>
                <Input
                  id="wr-pub-keywords"
                  value={form.keywords}
                  onChange={(e) => set({ keywords: e.target.value })}
                  placeholder="Read from the PDF if left blank"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="wr-pub-category">Category</Label>
                <Select value={form.category} onValueChange={(v) => set({ category: v })}>
                  <SelectTrigger id="wr-pub-category">
                    <SelectValue placeholder="Read from the PDF if left blank" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60 overflow-y-auto">
                    {flatCategories.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
        </div>
      )}

      {showWatermarkNote && (
        <p className="text-xs text-muted-foreground">
          On the free plan the compiled PDF carries the “Made with PaperVerse” footer, and that
          is the file published here.
        </p>
      )}

      <button
        type="button"
        onClick={onUseExistingPaper}
        className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
      >
        Add to an existing paper instead
      </button>
    </div>
  );
};

export default PublishDraftDialogBody;
